/**
 * Public share report `/r/<token>` (OutlierOS /r/[slug] replica, docs/legacy-ui/02 §4): hero with 5 stats, "All
 * reels" grid (Wise Score / Views / Newest, 60 at a time), embed-only modal with a locked hook box, CTA band.
 * Hooks, transcripts and video files never reach this page (the API strips them).
 */
import { LINK_GONE_TITLE } from '../../public-route';
import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Lock, Sparkles, X } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import type { ReportReel } from '@vw/core/src/report';
import { Button, EmptyState, ErrorState, Segmented, Spinner, TierBadge, formatWiseScore, type TierThresholds } from '@vw/ui';
import { Link2Off } from 'lucide-react';
import { api, ApiError } from '../../api';
import { CreatorAvatar } from '../../components/CreatorAvatar';
import { compact, igReel } from '../../format';
import { InstagramEmbed } from '../../components/InstagramEmbed';
import { NicheChips } from '../../components/NicheFilter';
import { PublicShell, signInHref } from './PublicShell';

interface PublicReport {
  creator: { igPk: string; handle: string | null; fullName: string | null; bio: string | null; avatarUrl: string | null; isVerified: boolean; baselineValue: number | null; niches?: Array<{ slug: string; name: string }> };
  window: string;
  outlierThreshold: number;
  tiers: TierThresholds;
  kpis: { reels: number; baselineViews: number | null; scoreBasis?: string | null };
  reels: ReportReel[];
}

const PAGE = 60;
const utcDate = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');

export function PublicReport({ token, signedIn }: { token: string; signedIn: boolean }) {
  const [r, setR] = useState<PublicReport | null>(null);
  const [gone, setGone] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useState<'outlier' | 'views' | 'newest'>('outlier');
  const [shown, setShown] = useState(PAGE);
  const [open, setOpen] = useState<ReportReel | null>(null);
  useEffect(() => {
    setFailed(null);
    // Only a 404 means the link is gone; anything else (rate limit, network) can be retried.
    api<PublicReport>(`/api/public/reports/${token}`).then(setR, (e) => (e instanceof ApiError && e.status === 404 ? setGone(true) : setFailed((e as Error).message)));
  }, [token, attempt]);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open]);
  const sorted = useMemo(() => {
    if (!r) return [];
    const key = (x: ReportReel) => (sort === 'outlier' ? x.outlierScore : sort === 'views' ? x.views : x.postedAt) ?? -1;
    return [...r.reels].sort((a, b) => key(b) - key(a));
  }, [r, sort]);

  const handle = r?.creator.handle ?? '';
  const who = handle ? `@${handle}` : 'This creator';
  const openPath = handle ? `/c/${handle}` : '/';
  const cta = { label: 'Analyze any creator free →', href: signInHref(handle ? `/c/${handle}` : '/') };
  if (gone) return <PublicShell cta={cta}><EmptyState icon={<Link2Off className="h-7 w-7" />} title={LINK_GONE_TITLE}>Ask the person who shared it for a new link.</EmptyState></PublicShell>;
  if (failed) return <PublicShell cta={null}><ErrorState message={`Could not load this report: ${failed}`} onRetry={() => setAttempt((n) => n + 1)} /></PublicShell>;
  if (!r) return <PublicShell cta={null}><Spinner label="Loading report…" /></PublicShell>;

  // Their own reels: collabs are scored against their usual views (decision 71) but aren't counted as theirs.
  const peak = r.reels.reduce((m, x) => (x.isCollab ? m : Math.max(m, x.outlierScore ?? 0)), 0);
  const viral = r.reels.filter((x) => !x.isCollab && (x.outlierScore ?? 0) >= r.outlierThreshold).length;
  const transcripts = r.reels.filter((x) => x.hasTranscript).length;
  const stat = (label: string, value: string) => (
    <div className="rounded-2xl bg-secondary/70 p-3 text-center"><div className="text-xl font-black tabular-nums">{value}</div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div></div>
  );

  return (
    <PublicShell cta={cta}>
      <section className="flex flex-col gap-6 rounded-3xl border border-border bg-card p-6 shadow-sm lg:flex-row lg:items-center sm:p-8">
        <div className="flex min-w-0 flex-1 items-start gap-4">
          <CreatorAvatar url={r.creator.avatarUrl} name={handle} size={80} rounded="2xl" />
          <div className="min-w-0 space-y-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Wise Score report</p>
            <h1 className="text-2xl font-black">{who}{r.creator.isVerified && <span className="ml-1 text-primary">✓</span>}</h1>
            {r.creator.fullName && r.creator.fullName.toLowerCase() !== handle && <p className="text-sm text-muted-foreground">{r.creator.fullName}</p>}
            {r.creator.bio && <p className="line-clamp-2 text-sm">{r.creator.bio}</p>}
            <NicheChips niches={r.creator.niches} className="pt-1" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {stat('Peak Wise Score', peak ? formatWiseScore(peak) : '–')}
          {stat(`Reels at ${r.outlierThreshold}×+`, String(viral))}
          {stat(r.kpis.scoreBasis === 'likes' ? 'Usual likes' : 'Usual views', compact(r.kpis.baselineViews))}
          {stat('Reels analysed', String(r.kpis.reels))}
          {stat('Transcripts', String(transcripts))}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><h2 className="text-lg font-extrabold">All reels ({r.reels.length})</h2>
            <p className="text-sm text-muted-foreground">Wise Score = reel views ÷ {handle ? `@${handle}'s` : "the creator's"} usual views. Click a reel to watch it.</p></div>
          <Segmented value={sort} onChange={(s) => { setSort(s); setShown(PAGE); }} options={[{ value: 'outlier', label: 'Wise Score' }, { value: 'views', label: 'Views' }, { value: 'newest', label: 'Newest' }]} />
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {sorted.slice(0, shown).map((x) => (
            <button key={x.pk} onClick={() => setOpen(x)} className="overflow-hidden rounded-2xl border border-border bg-card text-left shadow-xs transition-shadow hover:shadow-md">
              <div className="relative aspect-[9/16] bg-secondary">
                {x.thumbUrl && <img src={x.thumbUrl} alt="" referrerPolicy="no-referrer" loading="lazy" className="h-full w-full object-cover" />}
                <span className="absolute left-2 top-2"><TierBadge score={x.outlierScore} tiers={r.tiers} /></span>
                {x.hasTranscript && <span title="Transcript available after sign-in" className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-emerald-500/90 px-1.5 py-0.5 text-[10px] font-bold text-white"><Sparkles className="h-3 w-3" />Transcript</span>}
                <span className="absolute bottom-2 left-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-semibold text-white">▶ {compact(x.views)}</span>
              </div>
              <div className="space-y-1 p-3">
                <p className="line-clamp-3 text-xs">{x.caption || 'Watch reel'}</p>
                <p className="text-[11px] text-muted-foreground">❤ {x.likesHidden ? '–' : compact(x.likes)} · 💬 {compact(x.comments)} · {utcDate(x.postedAt)}</p>
              </div>
            </button>
          ))}
        </div>
        {sorted.length > shown && <div className="flex justify-center"><Button variant="secondary" onClick={() => setShown(shown + PAGE)}>Load more ({sorted.length - shown} left)</Button></div>}
      </section>

      <section className="rounded-3xl border border-primary/20 bg-primary/5 p-8 text-center">
        <h2 className="text-xl font-black">Find any creator's top Wise Score reels in one click</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">Open any creator's Reels tab with the {BRAND.name} extension to see which reels beat their usual views, with transcripts and spoken hooks.</p>
        <a href={cta.href} className="mt-4 inline-block rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Try {BRAND.name} free</a>
      </section>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && setOpen(null)}>
          <div role="dialog" aria-modal="true" aria-label="Reel" className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-y-auto rounded-2xl bg-card md:flex-row md:overflow-hidden">
            <button aria-label="Close" onClick={() => setOpen(null)} className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-1.5 text-white"><X className="h-4 w-4" /></button>
            <InstagramEmbed shortcode={open.shortcode} className="mx-auto aspect-[9/16] max-h-[60vh] w-full shrink-0 border-0 bg-white md:max-h-none md:w-[55%]" />
            <div className="space-y-3 overflow-y-auto p-5">
              <TierBadge score={open.outlierScore} tiers={r.tiers} />
              <p className="text-sm text-muted-foreground">▶ {compact(open.views)} · ❤ {open.likesHidden ? '–' : compact(open.likes)} · 💬 {compact(open.comments)}</p>
              {open.caption && <p className="line-clamp-[10] whitespace-pre-line text-sm">{open.caption}</p>}
              <div className="space-y-2 rounded-2xl border border-border bg-secondary/50 p-4">
                <p className="flex items-center gap-2 text-sm font-bold"><Lock className="h-4 w-4" />{open.hasTranscript ? 'Free transcript available' : 'Spoken hook & transcript'}</p>
                <p className="text-xs text-muted-foreground">{open.hasTranscript ? 'This reel is already transcribed. Sign in free to read its spoken hook and full transcript.' : 'Sign in free to get the spoken hook and the full transcript of this reel.'}</p>
                <a href={signedIn ? openPath : signInHref(openPath)} className="inline-block rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground">{signedIn ? `Open in ${BRAND.name} ↗` : 'Sign in free to read it'}</a>
              </div>
              <a href={igReel(open.shortcode)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary">Open on Instagram<ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
        </div>
      )}
    </PublicShell>
  );
}
